import { forwardRef } from 'react'
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react'
import { cx } from '@/lib/cx'

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'ghost' | 'danger' | 'outline'
  size?: 'sm' | 'md' | 'lg'
  loading?: boolean
}

const VARIANTS: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary: 'bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700',
  ghost: 'hover:bg-[var(--surface-sunken)]',
  danger: 'bg-red-500 text-white hover:bg-red-600',
  outline: 'border border-[var(--border)] hover:bg-[var(--surface-sunken)]',
}

const SIZES: Record<NonNullable<ButtonProps['size']>, string> = {
  sm: 'h-8 px-3 text-sm',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-6 text-base',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      {...rest}
      ref={ref}
      disabled={disabled || loading}
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-full font-medium',
        'transition-colors disabled:pointer-events-none disabled:opacity-50',
        'focus-visible:outline-brand-500 focus-visible:outline-2 focus-visible:outline-offset-2',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
    >
      {loading && <Spinner className="size-4" />}
      {children}
    </button>
  )
})

export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...rest}
      className={cx(
        'w-full rounded-xl border border-[var(--border)] bg-[var(--surface-raised)]',
        'px-4 py-2.5 text-[16px] placeholder:text-[var(--text-muted)]',
        'focus:outline-brand-500 focus:outline-2 focus:outline-offset-0',
        className,
      )}
    />
  )
}

export function Spinner({ className }: { className?: string }) {
  return (
    <svg className={cx('animate-spin', className)} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path
        d="M22 12a10 10 0 0 1-10 10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  )
}

const AVATAR_HUES = [12, 45, 92, 150, 200, 260, 300, 330]

/** Deterministic colour from the id, so a person looks the same everywhere. */
function hueFor(seed: string) {
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0
  return AVATAR_HUES[h % AVATAR_HUES.length]
}

export function Avatar({
  src,
  name,
  id,
  size = 40,
  presence,
}: {
  src?: string | null
  name: string
  id: string
  size?: number
  presence?: 'online' | 'away' | 'offline'
}) {
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')

  return (
    <span className="relative inline-block shrink-0" style={{ width: size, height: size }}>
      {src ? (
        <img
          src={src}
          alt=""
          width={size}
          height={size}
          loading="lazy"
          decoding="async"
          className="size-full rounded-full object-cover"
        />
      ) : (
        <span
          aria-hidden
          className="flex size-full items-center justify-center rounded-full font-semibold text-white"
          style={{
            background: `oklch(0.62 0.14 ${hueFor(id)})`,
            fontSize: size * 0.38,
          }}
        >
          {initials || '?'}
        </span>
      )}
      {presence && presence !== 'offline' && (
        <span
          className={cx(
            'absolute right-0 bottom-0 rounded-full ring-2',
            presence === 'online' ? 'bg-green-500' : 'bg-amber-400',
          )}
          style={{
            width: Math.max(8, size * 0.28),
            height: Math.max(8, size * 0.28),
            // @ts-expect-error -- CSS custom property
            '--tw-ring-color': 'var(--surface)',
          }}
        />
      )}
    </span>
  )
}

export function EmptyState({
  icon,
  title,
  hint,
  action,
}: {
  icon?: ReactNode
  title: string
  hint?: string
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-8 py-16 text-center">
      {icon && <div className="text-4xl opacity-60">{icon}</div>}
      <p className="font-medium">{title}</p>
      {hint && <p className="text-muted max-w-xs text-sm">{hint}</p>}
      {action}
    </div>
  )
}
